<script setup lang="ts">
import type { SizeVariants } from '~kui/types/kui';

import { Icon } from '~ds/components/ui/icon';
import { Heading } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';

interface Props {
  modelValue?: boolean;
  heading: string;
  omitBackground?: boolean;
  elevation?: Extract<SizeVariants, 'xs' | 'sm' | 'md' | 'lg'> | 'none';
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  omitBackground: false,
  elevation: 'sm',
});
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const accordionOpen = ref(false);

const accordionContent = useTemplateRef<HTMLDivElement>('accordionContent');

const toggleAccordion = () => {
  if (!accordionContent.value) {
    return;
  }
  accordionOpen.value = !accordionOpen.value;
  emit('update:modelValue', accordionOpen.value);
  if (accordionOpen.value) {
    accordionContent.value.style.height = `${accordionContent.value?.scrollHeight + 12}px`;
    return;
  }
  accordionContent.value.style.height = '0px';
};

const classList = computed(() => {
  return twMerge(
    cva('overflow-hidden py-2 ps-4 pe-2', {
      variants: {
        open: {
          true: 'rounded-4xl transition-[border-radius] delay-300 duration-500',
          false: 'rounded-4xl transition-[border-radius] delay-300 duration-500',
        },
      },
    })({
      open: accordionOpen.value,
    }),
  );
});

onMounted(() => {
  if (props.modelValue) {
    toggleAccordion();
  }
});

const accordionId = useId();
</script>

<template>
  <KUICoreCard
    :key="accordionId"
    :elevation="elevation"
    rounding="none"
    padding="none"
    :omit-background="omitBackground"
    :class="classList"
  >
    <KUICoreClickable
      class="w-full"
      :aria-expanded="accordionOpen"
      :aria-controls="`${accordionId}-content`"
      @click="toggleAccordion"
    >
      <div class="flex items-center justify-between gap-2">
        <Heading
          size="md"
          as="h3"
          weight="medium"
          class="py-3 text-left"
        >
          {{ heading }}
        </Heading>
        <span class="ml-auto flex size-16 shrink-0 grow-0 items-center justify-center rounded-full bg-secondary">
          <Icon
            :name="ICONS.plus"
            size="3xl"
            :class="{ 'rotate-45': accordionOpen }"
            class="transition-transform duration-500 ease-in-out"
          />
        </span>
      </div>
    </KUICoreClickable>
    <div
      :id="`${accordionId}-content`"
      ref="accordionContent"
      class="transition-height h-0 overflow-hidden duration-500 ease-in-out"
    >
      <div class="mt-2">
        <slot name="content" />
      </div>
    </div>
  </KUICoreCard>
</template>
